import { Suspense, useMemo } from "react";
import { lazyWithRetry as lazy } from "./lazyWithRetry";
import PropTypes from "prop-types";
import { Route, BrowserRouter as Router, Routes, useLocation } from "react-router-dom";

/** Components */
import Login from "../pages/auth/Login";
import Layouts from "../layouts/Layouts";
import ProtectedRoute from "./ProtectedRoute";
import NotFound from "../pages/notFound/NotFound";
import ResetPassword from "../pages/resetPassword/ResetPassword";
import ReportAccess from "../pages/Reports/ReportAccess";
import AuthorizedRoute from "./AuthorizedRoutes";
import ForgotPassword from "../pages/forgotPassword/ForgotPassword";
import { PageTitleProvider, usePageTitle } from "../context/PageTitleContext";

const PublicCandidateAssessment = lazy(() => import("../pages/candidateAssessment/publicCandidateAssessment/PublicCandidateAssessment"));
const CandidateFormLanding = lazy(() => import("../pages/candidateFormLanding/CandidateFormLanding"));
const CandidateFormFill = lazy(() => import("../pages/candidateFormFill/CandidateFormFill"));
const PublicJobs = lazy(() => import("../pages/publicJobs/PublicJobs"));
const CandidateOfferResponse = lazy(() => import("../pages/offers/CandidateOfferResponse"));
const CandidatePortalLogin = lazy(() => import("../pages/candidatePortal/CandidatePortalLogin"));
const CandidatePortalShell = lazy(() => import("../pages/candidatePortal/CandidatePortalShell"));
const CandidatePortalHome = lazy(() => import("../pages/candidatePortal/CandidatePortalHome"));
const CandidatePortalProfile = lazy(() => import("../pages/candidatePortal/CandidatePortalProfile"));
const CandidatePortalApplications = lazy(() => import("../pages/candidatePortal/CandidatePortalApplications"));
const CandidatePortalApplicationDetail = lazy(() => import("../pages/candidatePortal/CandidatePortalApplicationDetail"));
const CandidatePortalActions = lazy(() => import("../pages/candidatePortal/CandidatePortalActions"));
const CandidatePortalInterviews = lazy(() => import("../pages/candidatePortal/CandidatePortalInterviews"));
const CandidatePortalJobs = lazy(() => import("../pages/candidatePortal/CandidatePortalJobs"));
const CandidatePortalOffers = lazy(() => import("../pages/candidatePortal/CandidatePortalOffers"));
const CandidatePortalNotifications = lazy(() => import("../pages/candidatePortal/CandidatePortalNotifications"));

const resolvePublicPageTitle = (pathname) => {
  if (pathname === "/login") return "Login";
  if (pathname === "/forgot-password") return "Forgot Password";
  if (pathname.startsWith("/reset-password")) return "Reset Password";
  if (pathname.startsWith("/candidate-assessment/invite")) return "Candidate Assessment";
  if (pathname.startsWith("/offer-response")) return "Offer Response";
  if (pathname.startsWith("/form/") && pathname.endsWith("/fill")) return "Fill Form";
  if (pathname.startsWith("/form/")) return "Candidate Form";
  if (pathname.startsWith("/report/")) return "Report";
  if (pathname.startsWith("/candidate-portal")) return "Candidate Portal";
  if (pathname.startsWith("/public-jobs")) return "Public Jobs";
  return null;
};

const PublicRouteTitle = () => {
  const { pathname } = useLocation();
  const title = useMemo(() => resolvePublicPageTitle(pathname), [pathname]);
  usePageTitle(title);
  return null;
};
const AppRoutes = (props) => {
  return (
    <Router>
      <PageTitleProvider routes={props.componentRoutes}>
        <PublicRouteTitle />
      <Suspense
        fallback={
          <div>
            <div>Loading...</div>
          </div>
        }
      >
        <Routes>
          <Route
            name="Layout"
            path="/"
            element={
              <ProtectedRoute>
                <Layouts componentRoutes={props.componentRoutes.filter(r => !r.isFullScreen)} />
              </ProtectedRoute>
            }
          >
            {props.componentRoutes.filter(r => !r.isFullScreen).map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <AuthorizedRoute
                    component={route.component}
                    securityKey={route.securityKey}
                    moduleCode={route.moduleCode}
                  />
                }
              />
            ))}

            <Route path="report/:reportName" element={<ReportAccess />} />
            <Route path="*" name="Not Found" element={<NotFound />} />
          </Route>

          {/* Full Screen Routes */}
          {props.componentRoutes.filter(r => r.isFullScreen).map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={
                <ProtectedRoute>
                  <AuthorizedRoute
                    component={route.component}
                    securityKey={route.securityKey}
                    moduleCode={route.moduleCode}
                  />
                </ProtectedRoute>
              }
            />
          ))}

          <Route path="/login" name="Login" element={<Login />} />
          <Route path="/forgot-password" name="Forgot Password" element={<ForgotPassword />} />
          <Route path="/reset-password/:email" name="Reset Password" element={<ResetPassword />} />
          <Route path="/candidate-assessment/invite/:token" name="Candidate Assessment Invite" element={<PublicCandidateAssessment />} />
          <Route path="/offer-response" name="Offer Response" element={<CandidateOfferResponse />} />
          <Route path="/form/:token" name="Candidate Form Landing" element={<CandidateFormLanding />} />
          <Route path="/form/:token/fill" name="Candidate Form Fill" element={<CandidateFormFill />} />
          <Route path="/public-jobs" name="Public Jobs" element={<PublicJobs />} />
          <Route path="/candidate-portal/login" name="Candidate Portal Login" element={<CandidatePortalLogin />} />
          <Route path="/candidate-portal" element={<CandidatePortalShell />}>
            <Route index element={<CandidatePortalHome />} />
            <Route path="profile" element={<CandidatePortalProfile />} />
            <Route path="applications" element={<CandidatePortalApplications />} />
            <Route path="applications/:applicantId" element={<CandidatePortalApplicationDetail />} />
            <Route path="actions" element={<CandidatePortalActions />} />
            <Route path="interviews" element={<CandidatePortalInterviews />} />
            <Route path="jobs" element={<CandidatePortalJobs />} />
            <Route path="offers" element={<CandidatePortalOffers />} />
            <Route path="notifications" element={<CandidatePortalNotifications />} />
          </Route>
        </Routes>
      </Suspense>
      </PageTitleProvider>
    </Router>
  );
};

AppRoutes.propTypes = {
  componentRoutes: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      component: PropTypes.elementType.isRequired,
      securityKey: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ]),
      moduleCode: PropTypes.string,
    })
  ).isRequired,
};

export default AppRoutes;
